import { computed, onBeforeUnmount, onMounted, ref, type Ref } from 'vue';

/** Folga mínima entre o elemento e a borda da janela. */
const MARGEM = 8;
/**
 * Quanto o ponteiro precisa andar para o gesto virar arraste. Sem limiar, um
 * clique parado já converteria o elemento de ancorado-ao-canto para coordenada
 * fixa — e salvaria isso (mesma regra do `LigacaoOverlay`).
 */
const LIMIAR = 3;

interface Posicao { x: number; y: number }

/**
 * Elemento flutuante que o usuário pode arrastar para fora do caminho.
 *
 * - Enquanto ninguém arrastou, nenhum estilo inline: vale o canto do CSS.
 * - O punho é quem casa com `seletorPunho`; botões, links e campos NUNCA iniciam
 *   arraste (um arraste acidental engoliria o clique).
 * - A posição é preferência do posto de trabalho: fica no localStorage.
 * - Duplo clique no punho devolve ao canto. Resize da janela reencaixa.
 */
export function useArrastavel(elemento: Ref<HTMLElement | null>, opcoes: { chave: string; seletorPunho: string }) {
    const posicao = ref<Posicao | null>(ler(opcoes.chave));
    const arrastando = ref(false);
    let ponteiro: number | null = null;
    let origem: Posicao = { x: 0, y: 0 };
    let pegada: Posicao = { x: 0, y: 0 };
    let ultimoArrasteEm = 0;

    const estilo = computed<Record<string, string>>(() => {
        if (!posicao.value) return {} as Record<string, string>;
        return { left: `${posicao.value.x}px`, top: `${posicao.value.y}px`, right: 'auto', bottom: 'auto' };
    });

    function limitar(x: number, y: number): Posicao {
        const el = elemento.value;
        const w = el?.offsetWidth ?? 0;
        const h = el?.offsetHeight ?? 0;
        return {
            x: Math.min(Math.max(MARGEM, x), Math.max(MARGEM, window.innerWidth - w - MARGEM)),
            y: Math.min(Math.max(MARGEM, y), Math.max(MARGEM, window.innerHeight - h - MARGEM)),
        };
    }

    function aoPressionar(evento: PointerEvent) {
        if (ponteiro !== null) return;
        if (evento.pointerType === 'mouse' && evento.button !== 0) return;
        const alvo = evento.target as HTMLElement | null;
        if (!alvo?.closest(opcoes.seletorPunho)) return;
        // Controles não iniciam arraste — exceto quando o próprio punho é um botão
        // (a pílula recolhida do widget): aí o clique só vale se não arrastou.
        const interativo = alvo.closest('button, a, input, select, textarea, [data-sem-arraste]');
        if (interativo && !interativo.matches(opcoes.seletorPunho)) return;
        const el = elemento.value;
        if (!el) return;
        const caixa = el.getBoundingClientRect();
        origem = { x: evento.clientX, y: evento.clientY };
        pegada = { x: evento.clientX - caixa.left, y: evento.clientY - caixa.top };
        ponteiro = evento.pointerId;
        el.setPointerCapture(evento.pointerId);
    }

    function aoMover(evento: PointerEvent) {
        if (ponteiro !== evento.pointerId) return;
        if (evento.pointerType === 'mouse' && evento.buttons === 0) return aoSoltar(evento);
        if (!arrastando.value) {
            if (Math.hypot(evento.clientX - origem.x, evento.clientY - origem.y) < LIMIAR) return;
            arrastando.value = true;
        }
        posicao.value = limitar(evento.clientX - pegada.x, evento.clientY - pegada.y);
    }

    function aoSoltar(evento: PointerEvent) {
        if (ponteiro !== evento.pointerId) return;
        const moveu = arrastando.value;
        const el = elemento.value;
        if (el?.hasPointerCapture(evento.pointerId)) el.releasePointerCapture(evento.pointerId);
        arrastando.value = false;
        ponteiro = null;
        if (!moveu) return;
        ultimoArrasteEm = Date.now();
        salvar(opcoes.chave, posicao.value);
    }

    function aoPerderCaptura() {
        arrastando.value = false;
        ponteiro = null;
    }

    /** Duplo clique no punho: de volta ao canto padrão. */
    function aoDuploClique(evento: MouseEvent) {
        const alvo = evento.target as HTMLElement | null;
        if (!alvo?.closest(opcoes.seletorPunho) || alvo.closest('button, a, input')) return;
        // O 2º clique de um arraste rápido não é pedido de reset.
        if (Date.now() - ultimoArrasteEm < 400) return;
        posicao.value = null;
        salvar(opcoes.chave, null);
    }

    /** Janela encolheu (ou o conteúdo cresceu): traz de volta para dentro. */
    function reencaixar() {
        if (posicao.value) posicao.value = limitar(posicao.value.x, posicao.value.y);
    }

    onMounted(() => {
        window.addEventListener('resize', reencaixar);
        requestAnimationFrame(reencaixar);
    });
    onBeforeUnmount(() => window.removeEventListener('resize', reencaixar));

    /** O clique que encerra um arraste não é um clique de verdade. */
    function acabouDeArrastar() {
        return Date.now() - ultimoArrasteEm < 300;
    }

    return {
        estilo,
        arrastando,
        reencaixar,
        acabouDeArrastar,
        eventos: {
            onPointerdown: aoPressionar,
            onPointermove: aoMover,
            onPointerup: aoSoltar,
            onLostpointercapture: aoPerderCaptura,
            onDblclick: aoDuploClique,
        },
    };
}

function ler(chave: string): Posicao | null {
    try {
        const v = JSON.parse(localStorage.getItem(chave) || 'null');
        return v && Number.isFinite(v.x) && Number.isFinite(v.y) ? v : null;
    } catch {
        return null;
    }
}

function salvar(chave: string, p: Posicao | null) {
    try {
        if (p) localStorage.setItem(chave, JSON.stringify(p));
        else localStorage.removeItem(chave);
    } catch { /* sem storage: vale só nesta aba */ }
}
